import Button from '@orn/shared-web/components/Button/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { calloutToAlertRow } from '../../utils/alertRow';
import AlertListItem from '../AlertListItem';
import AlertSection from '../AlertSection';
import AlertTypeIcon from '../AlertTypeIcon';

import AlertsCenterEmptyState from './AlertsCenterEmptyState';
import AlertsCenterHeader, { ALERTS_CENTER_TITLE } from './AlertsCenterHeader';

import type { ListedAlert } from '../../utils/alertRow';
import type { AlertsCenterStatus, IAlertsCenterSection } from '../../utils/alertsCenter';

interface IAlertsCenterErrorStateProps {
  onRetry?: () => void;
}

/**
 * Neutral rather than `bg-error-*`: this is a fetch that did not come back, and
 * a red banner would read as something being wrong with the household's money.
 */
const AlertsCenterErrorState = ({ onRetry }: IAlertsCenterErrorStateProps) => (
  <div className="flex flex-col items-center justify-center gap-1 px-6 py-16 text-center">
    <div className="text-primary text-lg font-medium">We couldn’t load your alerts</div>
    <div className="text-tertiary text-sm">Check your connection and try again.</div>
    {onRetry && (
      <Button className="mt-5" size="sm" color="secondary" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

interface IAlertsCenterBaseProps {
  /** Groups with rows, already ordered and filtered — see `buildAlertsCenterView`. */
  sections: IAlertsCenterSection[];
  status: AlertsCenterStatus;
  onClose: () => void;
  /** Id of the row drawn with the brand border. */
  selectedId?: string | null;
  /** Id of the row showing the mid-turn refusal pill, or none — see `AlertListItem`. */
  blockedNoticeAlertId?: string | null;
  onSelect?: (alert: ListedAlert) => void;
  onDismiss?: (alertId: string) => void;
  /** Retries every failed read. Only reachable while `status` is `error`. */
  onRetry?: () => void;
  /** Rendered below the list — `alertActionSlot` from `useAlertRowAction`. */
  actionSlot?: React.ReactNode;
  /**
   * Reference instant for the rows' relative timestamps. Defaults to now; pass
   * a fixed date to make a story or test deterministic.
   */
  now?: Date;
  className?: string;
}

/**
 * The Alerts Center: a pinned header over the household's alerts, grouped into
 * need-attention, pending, and history. Purely presentational — {@link
 * AlertsCenter} owns the three reads, the counts, and the mutations.
 *
 * Renders in two places at the same size: the `xl+` side panel and, below `xl`,
 * the `/alerts` route body. The surfaces differ only in what `onClose` does, so
 * nothing here reads a breakpoint.
 *
 * Each group pages independently. The header count is the group's server-side
 * total, so it runs ahead of the rows on screen wherever a page is still
 * unloaded.
 */
const AlertsCenterBase = ({
  sections,
  status,
  onClose,
  selectedId,
  blockedNoticeAlertId,
  onSelect,
  onDismiss,
  onRetry,
  actionSlot,
  now,
  className,
}: IAlertsCenterBaseProps) => (
  <div className={cx('flex h-full min-h-0 w-full flex-col', className)} aria-label={ALERTS_CENTER_TITLE}>
    <AlertsCenterHeader onClose={onClose} />

    <div className="scrollbar-visible flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4">
      {status === 'loading' && (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <LoadingIndicator size="md" />
          <div className="text-tertiary text-sm">Loading your alerts…</div>
        </div>
      )}

      {status === 'error' && <AlertsCenterErrorState onRetry={onRetry} />}

      {status === 'empty' && <AlertsCenterEmptyState />}

      {status === 'ready' &&
        sections.map(({ section, count, rows, hasMore, isLoadingMore, onLoadMore }) => (
          <AlertSection key={section} section={section} count={count}>
            {rows.map((alert) => {
              const { title, subtitle, timestamp, status: rowStatus, tone } = calloutToAlertRow(alert, now);

              return (
                <AlertListItem
                  key={alert.id}
                  icon={<AlertTypeIcon callout={alert} />}
                  title={title}
                  subtitle={subtitle}
                  timestamp={timestamp}
                  status={rowStatus}
                  tone={tone}
                  selected={alert.id === selectedId}
                  showBlockedNotice={alert.id === blockedNoticeAlertId}
                  onClick={onSelect ? () => onSelect(alert) : undefined}
                  onDismiss={onDismiss ? () => onDismiss(alert.id) : undefined}
                />
              );
            })}
            {hasMore && onLoadMore && (
              <Button
                className="self-center"
                size="sm"
                color="tertiary"
                onClick={onLoadMore}
                isLoading={isLoadingMore}
                isDisabled={isLoadingMore}
              >
                Show more
              </Button>
            )}
          </AlertSection>
        ))}
    </div>

    {/* Outside the scroller so a failed reconnect stays put while the list
        scrolls. Its own `mx-4` matches the body's inset. */}
    {actionSlot}
  </div>
);

export default AlertsCenterBase;
